<template>
  <div class="flex flex-col gap-4">
    <div class="flex gap-4 items-center justify-between">
      <div class="flex gap-4 items-center">
        <button-select
          v-model="gridView"
          :options="options.gridView"
          size="sm"
        />

        <button-select
          v-model="filters.is_compressed"
          :options="options.is_compressed"
          size="sm"
          @input="fetchRecords()"
        />
      </div>
      <div class="flex gap-2 items-center">
        <div v-if="stats" class="flex gap-2 items-center">
          <div>
            {{ stats.compressed_image_count | format }}
            /
            {{ stats.total_image_count | format }} compressed
          </div>
          <div>
            {{ formatFileSize(stats.bytes_uncompressed) }}
            ->
            {{ formatFileSize(stats.bytes_compressed) }}
            ({{ formatFileSize(stats.bytes_saved) }} saved)
            ({{ stats.percentage_saved | round }}% saved)
          </div>
        </div>
        <div>
          <b-btn
            variant="primary"
            size="sm"
            @click="compressAll"
            :disabled="isLoading"
          >
            Compress All
          </b-btn>
        </div>
      </div>
    </div>

    <div v-if="gridView">
      <gallery :files="images" readonly />
    </div>

    <table v-else class="table table-google table-highlight">
      <thead>
        <tr>
          <th>File</th>
          <th class="text-nowrap">Size (original)</th>
          <th>Size</th>
          <th>Saved</th>
          <th>Uploaded</th>
          <th>Compressed</th>
          <th>Uploader</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="image in images" :key="`table-${image.id}`">
          <td>{{ image.filename }}</td>
          <td>
            <div v-if="image.bytes_original">{{ formatFileSize(image.bytes_original) }}</div>
            <div v-else>-</div>
          </td>
          <td class="text-nowrap">
            <div>{{ formatFileSize(image.file_size) }}</div>
          </td>
          <td class="text-nowrap">
            <div v-if="image.bytes_saved" class="flex gap-2 items-center justify-between">
              <div>{{ formatFileSize(image.bytes_saved) }}</div>
              <div>-{{ ((image.bytes_saved / image.bytes_original) * 100).toFixed(2) }}%</div>
            </div>
            <div v-else>-</div>
          </td>
          <td class="text-nowrap">{{ image.upload_ts | longdate }} {{ image.upload_ts | time }}</td>
          <td class="text-nowrap">
            <span v-if="image.compressed_at">
              {{ image.compressed_at | longdate }} {{ image.compressed_at | time }}
            </span>
            <span v-else>-</span>
          </td>
          <td>{{ image.upload_user }}</td>
          <td>
            <div class="flex gap-2 items-center">
              <b-btn
                variant="white"
                size="sm"
                @click="previewImage(image)"
              >
                Preview
              </b-btn>

              <b-btn
                variant="primary"
                size="sm"
                :disabled="isLoading || image.compressed_at !== null || image.busy"
                @click="compressImage(image)"
              >
                Compress
              </b-btn>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <b-pagination
      v-model="filters.page"
      :total-rows="rowCount"
      :per-page="filters.per_page"
      @input="fetchRecords(true)"
      :disabled="isLoading"
      hide-ellipsis
      limit="7"
    ></b-pagination>
  </div>
</template>

<script>
import { formatFileSize } from '@/helpers';

const ButtonSelect = () => import('@/components/global/ButtonSelect.vue');
const Gallery = () => import('@/components/global/gallery/Gallery.vue');

export default {
  name: 'Images',
  components: {
    ButtonSelect,
    Gallery,
  },
  computed: {
    isLoading() {
      return this.loadingCount > 0;
    },
  },
  data() {
    return {
      loadingCount: 0,
      images: [],
      rowCount: 0,
      stats: null,
      gridView: false,
      filters: {
        page: 1,
        per_page: 45,
        is_compressed: null,
      },
      options: {
        is_compressed: [
          { text: 'All', value: null },
          { text: 'Compressed', value: '1' },
          { text: 'Uncompressed', value: '0' },
        ],
        gridView: [
          { text: 'Table', value: false },
          { text: 'Grid (View-only)', value: true },
        ],
      },
    };
  },
  methods: {
    previewImage(image) {
      const w = 550;
      const h = 550;
      const y = window.top.outerHeight / 2 + window.top.screenY - (h / 2);
      const x = window.top.outerWidth / 2 + window.top.screenX - (w / 2);
      const url = `${location.origin}/api/upload/${image.id}/${image.filename}`;
      const popoutWindow = window.open(url, 'Image', `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${w}, height=${h}, top=${y}, left=${x}`);
      popoutWindow.focus();
    },
    compressAll() {
      this.loadingCount++;
      this.$http
        .post('/images/compress')
        .then(() => {
          this.fetchAll();
        })
        .catch((err) => {
          this.$toast.error(`Failed to compress images: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    compressImage(image) {
      image.busy = true;
      this.$http
        .post(`/images/${image.id}/compress`)
        .then((res) => {
          image.file_size = res.body.file_size;
          image.mtime_ts = res.body.mtime_ts;
          image.bytes_original = res.body.bytes_original;
          image.bytes_saved = res.body.bytes_saved;
          image.compressed_at = res.body.compressed_at;
          this.fetchStats();
        })
        .catch((err) => {
          this.$toast.error(`Failed to compress image: ${err.response.text}`);
        })
        .finally(() => {
          image.busy = false;
        });
    },
    fetchStats() {
      this.$http
        .get('/images/stats')
        .then((res) => {
          this.stats = res.body;
        })
        .catch((err) => {
          this.$toast.error(`Failed to fetch stats: ${err.response.text}`);
        });
    },
    fetchRecordsData() {
      this.loadingCount++;
      this.$http
        .get('/images')
        .query(this.filters)
        .then((res) => {
          this.images = res.body.map((image) => ({
            ...image,
            busy: false,
          }));
        })
        .catch((err) => {
          this.$toast.error(`Failed to fetch records: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    fetchRecordCount() {
      this.loadingCount++;
      this.$http
        .get('/images/count')
        .query(this.filters)
        .then((res) => {
          this.rowCount = res.body;
        })
        .catch((err) => {
          this.$toast.error(`Failed to fetch record count: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    fetchRecords(paginate) {
      paginate = paginate === undefined ? false : paginate;
      if (paginate === false) {
        this.fetchRecordCount();
        this.filters.page = 1;
      } else {
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 50);
      }
      this.fetchRecordsData();
    },
    fetchAll() {
      this.fetchRecords();
      this.fetchStats();
    },
    formatFileSize,
  },
  created() {
    this.fetchAll();
  },
};
</script>

<style lang="scss" scoped>
</style>
